/**
 * 1.3.74: MVP 付费功能 - 订阅状态卡片组件
 * 显示当前套餐、试用期、订阅状态等信息
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Crown, 
  Clock, 
  CreditCard, 
  ExternalLink, 
  AlertTriangle,
  CheckCircle,
  Building2,
  User,
  Loader2,
  Minus,
  Plus,
  Users
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import type { SubscriptionInfo } from '../types/billing';
import { 
  SUBSCRIPTION_STATUS_CONFIG,
  PLAN_DISPLAY_CONFIGS,
} from '../types/billing';
import {
  getTrialDaysRemaining,
  isInTrial,
  isTrialExpired,
  hasActiveSubscription,
  getEffectiveSubscriptionStatus,
  redirectToPortal,
  updateSubscriptionQuantity,
  resumeSubscription,
  formatDate,
  formatSubscriptionPeriod,
  formatPrice,
} from '../services/billingService';

interface SubscriptionCardProps {
  subscription: SubscriptionInfo | null;
  isLoading?: boolean;
  onUpgrade?: () => void;
  showUpgradeButton?: boolean;
  onSubscriptionUpdate?: () => void; // 1.3.79: 订阅更新后的回调
}

// 1.3.97: 计算使用百分比
const getUsagePercent = (used: number, max: number): number => {
  if (max <= 0) return 0;
  return Math.round((used / max) * 100);
};

// 1.3.97: 格式化存储显示 (XX/10GB)
const formatStorageDisplay = (usedGb?: number, maxGb?: number): string => {
  const used = usedGb ?? 0;
  const max = maxGb ?? 0;
  
  // 如果使用量小于1GB，显示MB
  if (used < 1 && used > 0) {
    const usedMb = Math.round(used * 1024);
    return `${usedMb}MB/${max}GB`;
  }
  
  return `${used.toFixed(1)}/${max}GB`;
};

// 1.3.97: 格式化公开链接显示 (XX/10K)
const formatPublicLinkDisplay = (used?: number, quota?: number): string => {
  const usedCount = used ?? 0;
  const maxCount = quota ?? 0;
  
  // 格式化使用量
  const formatCount = (count: number): string => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(count % 1000 === 0 ? 0 : 1)}K`;
    }
    return count.toString();
  };
  
  // 格式化配额
  const formatQuota = (q: number): string => {
    if (q >= 1000) {
      return `${(q / 1000).toFixed(0)}K`;
    }
    return q.toString();
  };
  
  return `${formatCount(usedCount)}/${formatQuota(maxCount)}`;
};

export function SubscriptionCard({
  subscription,
  isLoading = false,
  onUpgrade,
  showUpgradeButton = true,
  onSubscriptionUpdate,
}: SubscriptionCardProps) {
  const { t } = useTranslation();
  const [isRedirecting, setIsRedirecting] = useState(false);
  
  // 1.3.79: 人数调整状态
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [newQuantity, setNewQuantity] = useState(subscription?.purchased_seats || 2);
  
  // 1.3.81: 恢复订阅状态
  const [isResuming, setIsResuming] = useState(false);
  const [isUpdatingQuantity, setIsUpdatingQuantity] = useState(false);

  // 获取当前套餐配置
  const currentPlan = PLAN_DISPLAY_CONFIGS.find(
    (p) => p.id === subscription?.plan_type
  ) || PLAN_DISPLAY_CONFIGS[0];

  // 1.6.57: 获取有效订阅状态（优先使用管理员覆盖状态）
  const effectiveStatus = getEffectiveSubscriptionStatus(subscription);
  
  // 获取订阅状态配置 - 使用有效状态
  const statusConfig = SUBSCRIPTION_STATUS_CONFIG[effectiveStatus] || SUBSCRIPTION_STATUS_CONFIG['none'];
  
  // 1.6.57: 检查是否有管理员覆盖
  const hasAdminOverride = subscription?.admin_override_subscription_status != null;
  const isInternalAccount = subscription?.is_internal === true;

  // 计算试用剩余天数
  const trialDays = getTrialDaysRemaining(subscription);
  const inTrial = isInTrial(subscription);
  const trialExpired = isTrialExpired(subscription);
  const hasSubscription = hasActiveSubscription(subscription);

  // 处理管理订阅点击
  // 1.3.79: 新 tab 打开后需要重置 loading 状态
  const handleManageSubscription = async () => {
    if (!subscription?.organization_id) return;
    
    setIsRedirecting(true);
    const error = await redirectToPortal(subscription.organization_id);
    
    if (error) {
      toast.error(error.message);
    }
    // 1.3.79: 无论成功失败都重置状态（因为现在是新 tab 打开）
    setIsRedirecting(false);
  };

  // 1.3.81: 恢复订阅（取消"周期结束后取消"）
  const handleResumeSubscription = async () => {
    if (!subscription?.organization_id) return;
    
    setIsResuming(true);
    
    const { result, error } = await resumeSubscription(subscription.organization_id);
    
    if (error) {
      toast.error(error.message);
    } else if (result) {
      toast.success(t('subscriptionResumed', '订阅已恢复，将继续自动续费'));
      // 刷新订阅信息
      onSubscriptionUpdate?.();
    }
    
    setIsResuming(false);
  };

  // 1.3.79: 打开人数调整弹窗
  const handleOpenQuantityModal = () => {
    setNewQuantity(subscription?.purchased_seats || 2);
    setShowQuantityModal(true);
  };

  // 1.3.79: 更新人数
  const handleUpdateQuantity = async () => {
    if (!subscription?.organization_id) return;
    
    const currentQuantity = subscription?.purchased_seats || 2;
    if (newQuantity === currentQuantity) {
      setShowQuantityModal(false);
      return;
    }

    setIsUpdatingQuantity(true);
    
    const { result, error } = await updateSubscriptionQuantity(
      subscription.organization_id,
      newQuantity
    );
    
    if (error) {
      toast.error(error.message);
    } else if (result) {
      toast.success(result.message);
      setShowQuantityModal(false);
      // 刷新订阅信息
      onSubscriptionUpdate?.();
    }
    
    setIsUpdatingQuantity(false);
  };

  // 计算人数变化的费用预估
  const calculateQuantityChange = () => {
    const currentQuantity = subscription?.purchased_seats || 2;
    const diff = newQuantity - currentQuantity;
    const pricePerUser = 2300; // Standard 单价
    
    if (diff > 0) {
      return {
        type: 'increase' as const,
        diff,
        estimatedCost: diff * pricePerUser,
        message: t('increaseSeatsMessage', '增加 {{count}} 个席位，将立即按比例收费约 {{price}}', {
          count: diff,
          price: formatPrice(diff * pricePerUser)
        })
      };
    } else if (diff < 0) {
      return {
        type: 'decrease' as const,
        diff: Math.abs(diff),
        estimatedCost: 0,
        message: t('decreaseSeatsMessage', '减少 {{count}} 个席位，将从下个计费周期开始生效', {
          count: Math.abs(diff)
        })
      };
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="space-y-3">
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      {/* 标题 */}
      <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Crown className="w-5 h-5 text-primary" />
        {t('subscriptionAndPlan', '订阅与套餐')}
      </h2>

      {/* 当前套餐信息 */}
      <div className="space-y-4">
        {/* 套餐名称和状态 - 1.3.80: 多语言支持 */}
        {/* 1.4.2: 企业用户显示实际月费总额 */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-lg font-medium text-gray-900">
                {t(currentPlan.nameKey, currentPlan.id)}
              </span>
              {currentPlan.id !== 'free' && (
                <span className="text-sm text-gray-500">
                  {/* 1.4.2: 标题行显示简洁价格，详细金额在下方活跃订阅区域显示 */}
                  ({formatPrice(currentPlan.price, currentPlan.id === 'standard')})
                </span>
              )}
            </div>
          </div>
          
          {/* 状态徽章 - 1.3.80: 多语言支持 */}
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusConfig.bgColor} ${statusConfig.color}`}>
            {t(statusConfig.labelKey, statusConfig.labelFallback)}
          </span>
        </div>

        {/* 组织类型 */}
        <div className="flex items-center gap-2 text-gray-600">
          {subscription?.org_type === 'business' ? (
            <>
              <Building2 className="w-4 h-4" />
              <span>{t('businessOrganization', '企业组织')}</span>
            </>
          ) : (
            <>
              <User className="w-4 h-4" />
              <span>{t('personalWorkspace', '个人工作区')}</span>
            </>
          )}
        </div>

        {/* 试用期提示 */}
        {inTrial && (
          <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
            <Clock className="w-5 h-5 text-blue-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-blue-700 font-medium">
                {t('trialPeriod', '免费试用中')}
              </p>
              <p className="text-blue-600 text-sm">
                {t('trialDaysRemaining', '剩余 {{days}} 天', { days: trialDays })}
                {subscription?.trial_ends_at && (
                  <span className="ml-2 text-blue-500">
                    ({t('expiresOn', '到期日')}: {formatDate(subscription.trial_ends_at)})
                  </span>
                )}
              </p>
            </div>
          </div>
        )}

        {/* 试用过期提示 */}
        {trialExpired && !hasSubscription && (
          <div className="flex items-center gap-2 p-3 bg-orange-50 rounded-lg border border-orange-100">
            <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-orange-700 font-medium">
                {t('trialExpired', '试用期已结束')}
              </p>
              <p className="text-orange-600 text-sm">
                {t('upgradeToUnlock', '升级套餐以解锁全部功能')}
              </p>
            </div>
          </div>
        )}

        {/* 1.6.57: 内部账号/管理员覆盖提示 */}
        {(isInternalAccount || hasAdminOverride) && (
          <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg border border-purple-100">
            <Crown className="w-5 h-5 text-purple-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-purple-700 font-medium">
                {isInternalAccount 
                  ? t('internalAccount', '内部账号') 
                  : t('adminOverride', '管理员授权')}
              </p>
              <p className="text-purple-600 text-sm">
                {isInternalAccount
                  ? t('internalAccountDesc', '此账号由管理员设置为内部账号，享受完整功能')
                  : t('adminOverrideDesc', '订阅状态由管理员手动设置')}
              </p>
              {subscription?.admin_override_until && (
                <p className="text-purple-500 text-xs mt-1">
                  {t('validUntil', '有效期至')}: {formatDate(subscription.admin_override_until)}
                </p>
              )}
            </div>
          </div>
        )}

        {/* 活跃订阅信息 - 1.4.2: 添加月费显示 */}
        {hasSubscription && (
          <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-100">
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-green-700 font-medium">
                {t('subscriptionActive', '订阅已激活')}
              </p>
              {/* 1.6.57: 如果是管理员覆盖，不显示 Stripe 周期信息 */}
              {!hasAdminOverride && subscription?.subscription_current_period_start && subscription?.subscription_current_period_end && (
                <p className="text-green-600 text-sm">
                  {t('currentPeriod', '当前周期')}: {formatSubscriptionPeriod(
                    subscription.subscription_current_period_start,
                    subscription.subscription_current_period_end
                  )}
                </p>
              )}
              {subscription?.subscription_cancel_at_period_end && (
                <p className="text-orange-600 text-sm mt-1">
                  {t('cancelAtPeriodEnd', '将在周期结束后取消')}
                </p>
              )}
            </div>
            {/* 1.4.2: 显示实际月费金额 */}
            {subscription?.plan_type === 'standard' && subscription?.purchased_seats && (
              <div className="text-right flex-shrink-0">
                <p className="text-xl font-bold text-primary">
                  {formatPrice(subscription.purchased_seats * 2300)}
                </p>
                <p className="text-xs text-gray-500">
                  {subscription.purchased_seats}{t('seats', '人')} × ¥2,300
                </p>
              </div>
            )}
            {subscription?.plan_type === 'individual' && (
              <div className="text-right flex-shrink-0">
                <p className="text-xl font-bold text-primary">
                  {formatPrice(2500)}
                </p>
              </div>
            )}
          </div>
        )}

        {/* 待支付提示 */}
        {subscription?.subscription_status === 'past_due' && (
          <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg border border-red-100">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-red-700 font-medium">
                {t('paymentPastDue', '支付失败')}
              </p>
              <p className="text-red-600 text-sm">
                {t('updatePaymentMethod', '请更新支付方式以继续使用服务')}
              </p>
            </div>
          </div>
        )}

        {/* 配额信息 - 1.3.97: 显示使用量/配额 格式 */}
        <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-100">
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">
              {/* 1.3.79: Standard 显示 已购买/最大限制，其他显示上限 */}
              {subscription?.plan_type === 'standard' 
                ? `${subscription?.purchased_seats || 2}/50`
                : (subscription?.max_members || 1)
              }
            </p>
            <p className="text-sm text-gray-500">
              {subscription?.plan_type === 'standard' 
                ? t('purchasedSeats', '已购买席位')
                : t('maxMembers', '成员上限')
              }
            </p>
            {/* 1.3.79: Standard 套餐显示调整按钮 */}
            {subscription?.plan_type === 'standard' && hasSubscription && (
              <button
                onClick={handleOpenQuantityModal}
                className="mt-2 text-xs text-primary hover:text-primary-dark flex items-center justify-center gap-1 mx-auto"
              >
                <Users className="w-3 h-3" />
                {t('adjustSeats', '调整人数')}
              </button>
            )}
          </div>
          {/* 1.3.97: 存储空间 - 显示使用量/配额 */}
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">
              {formatStorageDisplay(subscription?.storage_used_gb, subscription?.max_storage_gb)}
            </p>
            <p className="text-sm text-gray-500">{t('storageUsage', '用量统计')}</p>
            {/* 存储使用进度条 */}
            {subscription?.max_storage_gb && subscription.max_storage_gb > 0 && (
              <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                <div 
                  className={`h-1.5 rounded-full transition-all ${
                    getUsagePercent(subscription?.storage_used_gb || 0, subscription.max_storage_gb) >= 90 
                      ? 'bg-red-500' 
                      : getUsagePercent(subscription?.storage_used_gb || 0, subscription.max_storage_gb) >= 70 
                        ? 'bg-orange-500' 
                        : 'bg-primary'
                  }`}
                  style={{ width: `${Math.min(100, getUsagePercent(subscription?.storage_used_gb || 0, subscription.max_storage_gb))}%` }}
                />
              </div>
            )}
          </div>
          {/* 1.3.97: 公开访问 - 显示使用量/配额 */}
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">
              {formatPublicLinkDisplay(subscription?.public_link_used, subscription?.public_link_quota)}
            </p>
            <p className="text-sm text-gray-500">{t('publicVisits', '访问次数统计')}</p>
            {/* 公开访问使用进度条 */}
            {subscription?.public_link_quota && subscription.public_link_quota > 0 && (
              <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                <div 
                  className={`h-1.5 rounded-full transition-all ${
                    getUsagePercent(subscription?.public_link_used || 0, subscription.public_link_quota) >= 90 
                      ? 'bg-red-500' 
                      : getUsagePercent(subscription?.public_link_used || 0, subscription.public_link_quota) >= 70 
                        ? 'bg-orange-500' 
                        : 'bg-primary'
                  }`}
                  style={{ width: `${Math.min(100, getUsagePercent(subscription?.public_link_used || 0, subscription.public_link_quota))}%` }}
                />
              </div>
            )}
          </div>
        </div>

        {/* 操作按钮 */}
        <div className="flex gap-3 pt-4">
          {/* 1.3.81: 升级按钮 - 根据套餐和状态显示不同文字
           * 试用中个人账号：显示"升级账号"（可选择 Individual 或 Standard）
           * 付费个人账号：显示"升级到 Standard"（只能选择 Standard）
           * 试用中企业账号：显示"升级账号"（选择 Standard 并选人数）
           * 付费中企业账号：不显示升级按钮（通过调整人数按钮操作）
           */}
          {showUpgradeButton && onUpgrade && (() => {
            const isTrialing = subscription?.subscription_status === 'trialing';
            const isActive = subscription?.subscription_status === 'active';
            const isIndividual = subscription?.plan_type === 'individual';
            const isStandard = subscription?.plan_type === 'standard';
            
            // 付费中企业账号不显示升级按钮（已经是最高套餐）
            if (isStandard && isActive) {
              return null;
            }
            
            // 其他情况显示升级按钮
            let buttonText = t('upgradePlan', '升级套餐');
            
            if (isIndividual && isActive) {
              // 付费个人账号：升级到 Standard
              buttonText = t('upgradeToBusiness', '升级到 Standard');
            } else if (isTrialing) {
              // 试用中（个人或企业）：升级账号
              buttonText = t('upgradeAccount', '升级账号');
            }
            
            return (
              <button
                onClick={onUpgrade}
                className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors flex items-center justify-center gap-2"
              >
                <Crown className="w-4 h-4" />
                {buttonText}
              </button>
            );
          })()}

          {/* 1.3.81: 重新订阅按钮（当订阅即将取消时显示） */}
          {subscription?.subscription_cancel_at_period_end && subscription?.has_stripe_customer && (
            <button
              onClick={handleResumeSubscription}
              disabled={isResuming}
              className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isResuming ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Crown className="w-4 h-4" />
                  {t('resubscribe', '重新订阅')}
                </>
              )}
            </button>
          )}

          {/* 管理订阅按钮（仅已订阅用户显示） */}
          {subscription?.has_stripe_customer && (
            <button
              onClick={handleManageSubscription}
              disabled={isRedirecting}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isRedirecting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <CreditCard className="w-4 h-4" />
                  {t('manageSubscription', '管理订阅')}
                  <ExternalLink className="w-3 h-3" />
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* 1.3.79: 调整人数弹窗 */}
      {showQuantityModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
            {/* 弹窗标题 */}
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                {t('adjustTeamSize', '调整团队人数')}
              </h3>
            </div>

            {/* 弹窗内容 */}
            <div className="px-6 py-6">
              {/* 当前人数 */}
              <div className="text-center mb-6">
                <p className="text-sm text-gray-500 mb-2">
                  {t('currentSeats', '当前人数')}: {subscription?.purchased_seats || 2} {t('seats', '人')}
                </p>
              </div>

              {/* 人数选择器 */}
              <div className="flex items-center justify-center gap-4 mb-6">
                <button
                  onClick={() => setNewQuantity(Math.max(2, newQuantity - 1))}
                  disabled={newQuantity <= 2}
                  className="w-12 h-12 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                >
                  <Minus className="w-5 h-5 text-gray-600" />
                </button>
                
                <div className="text-center">
                  <p className="text-4xl font-bold text-gray-900">{newQuantity}</p>
                  <p className="text-sm text-gray-500">{t('seats', '人')}</p>
                </div>
                
                <button
                  onClick={() => setNewQuantity(Math.min(50, newQuantity + 1))}
                  disabled={newQuantity >= 50}
                  className="w-12 h-12 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                >
                  <Plus className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {/* 费用变化提示 */}
              {calculateQuantityChange() && (
                <div className={`p-4 rounded-lg mb-6 ${
                  calculateQuantityChange()?.type === 'increase' 
                    ? 'bg-blue-50 border border-blue-100' 
                    : 'bg-orange-50 border border-orange-100'
                }`}>
                  <p className={`text-sm ${
                    calculateQuantityChange()?.type === 'increase' 
                      ? 'text-blue-700' 
                      : 'text-orange-700'
                  }`}>
                    {calculateQuantityChange()?.message}
                  </p>
                  {calculateQuantityChange()?.type === 'increase' && (
                    <p className="text-xs text-blue-500 mt-1">
                      {t('proratedBilling', '按当前计费周期剩余天数按比例计算')}
                    </p>
                  )}
                </div>
              )}

              {/* 月费预估 */}
              <div className="text-center text-sm text-gray-500">
                {t('newMonthlyFee', '调整后月费')}: 
                <span className="font-semibold text-gray-900 ml-1">
                  {formatPrice(newQuantity * 2300)}
                </span>
              </div>
            </div>

            {/* 弹窗按钮 */}
            <div className="px-6 py-4 bg-gray-50 flex gap-3">
              <button
                onClick={() => setShowQuantityModal(false)}
                disabled={isUpdatingQuantity}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                {t('cancel', '取消')}
              </button>
              <button
                onClick={handleUpdateQuantity}
                disabled={isUpdatingQuantity || newQuantity === (subscription?.purchased_seats || 2)}
                className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isUpdatingQuantity ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  t('confirm', '确认')
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
